import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_LINEUPS_ARENA_FIXTURE,
  ANY_SPORT_LINEUPS_PRO,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { ModeIcon } from '#src/components/ModeIcon/index.tsx';
import { useCurrentLineupMode } from '#src/hooks/useCurrentLineupMode.ts';
import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';
import { LineupMode, Mode, modeColors } from '#src/lib/gameMode.ts';

type Props = {
  fixture?: string;
};

const Switcher = styled(Horizontal).attrs({ gap: 1, center: true })`
  justify-content: center;
`;

const Pill = styled(ButtonBase)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);
  height: calc(var(--quadruple-unit) + var(--unit));
  min-width: calc(var(--quadruple-unit) + var(--unit));
  padding: 0 var(--double-unit);
  border-radius: 999px;
  color: var(--c-white);
  background: var(--c-nd-100-opaque);
  border: 1px solid var(--c-nd-150);
  cursor: pointer;

  &:disabled {
    cursor: default;
    color: var(--c-nd-500);
  }

  &.selected {
    background:
      linear-gradient(
        180deg,
        rgba(var(--pill-rgb), 0.25),
        rgba(var(--pill-rgb), 0.05)
      ),
      var(--c-nd-100-opaque);
    border-color: var(--pill-color);
    box-shadow: 0 0 16px -6px var(--pill-color);
  }

  view-transition-class: stretch;
`;

const IconWrapper = styled.span`
  display: inline-flex;
`;

type PillConfig = {
  mode: LineupMode;
  label: ReactNode;
  icon: ReactNode;
  color: string;
  colorRgb: string;
  to?: string;
};

export const LineupsModeSwitcher = ({ fixture = 'ongoing' }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { updateBoardView } = useLastVisitedBoardView();
  const currentMode = useCurrentLineupMode();

  const pills: PillConfig[] = [
    {
      mode: LineupMode.PRO,
      label: <FormattedMessage defaultMessage="Pro" />,
      icon: <ModeIcon mode={Mode.PRO} size={2.5} />,
      color: modeColors[Mode.PRO],
      colorRgb: 'var(--c-rgb-inventory-orange)',
      to: generateSportContextPath(ANY_SPORT_LINEUPS_PRO),
    },
    {
      mode: LineupMode.ARENA,
      label: <FormattedMessage defaultMessage="Arena" />,
      icon: <ModeIcon mode={Mode.ARENA_CASH} size={2.5} />,
      color: modeColors[Mode.ARENA_CASH],
      colorRgb: 'var(--c-rgb-unique)',
      to: generateSportContextPath(ANY_SPORT_LINEUPS_ARENA_FIXTURE, {
        params: { fixture },
      }),
    },
  ];

  const onSelect = (mode: LineupMode) => {
    if (mode === LineupMode.PRO) updateBoardView({ lastVisitedMode: Mode.PRO });
    if (mode === LineupMode.ARENA)
      updateBoardView({ lastVisitedMode: Mode.ARENA_CASH });
  };

  return (
    <Switcher>
      {pills.map(({ mode, label, icon, color, colorRgb, to }) => {
        const selected = mode === currentMode;
        const transitionId = `lineups-mode-${mode.toLowerCase()}`;
        return (
          <Pill
            key={mode}
            to={to}
            disabled={!to}
            className={selected ? 'selected' : undefined}
            onClick={() => onSelect(mode)}
            style={
              {
                '--pill-color': color,
                '--pill-rgb': colorRgb,
                viewTransitionName: transitionId,
              } as CSSProperties
            }
          >
            <IconWrapper style={{ viewTransitionName: `${transitionId}-icon` }}>
              {icon}
            </IconWrapper>
            {selected ? (
              <BrandS
                color="var(--c-white)"
                style={{ viewTransitionName: `${transitionId}-label` }}
              >
                {label}
              </BrandS>
            ) : null}
          </Pill>
        );
      })}
    </Switcher>
  );
};
